<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { fly, scale } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import { showAIChat, aiChatActive, aiModeActive } from '$lib/stores/ai';
  import { summaryBarHeight } from '$lib/stores/ui';
  import AIChatContent from './AIChatContent.svelte';

  let chatContent: AIChatContent;

  /**
   * How far off the bottom of the window these float.
   *
   * Measured from the summary bar rather than guessed at. Both of these used
   * to carry their own constant — `bottom-20 sm:bottom-24` on the launcher,
   * `bottom-16 sm:bottom-20` on the panel — which is two different guesses at
   * one number, and the number is not constant: the bar is 65px, 123px or
   * 182px depending on the width and whether its too-large notice is up. On a
   * phone the launcher sat 43px inside it.
   *
   * `+ 16` is the gap, matching the `right-4` on the other axis.
   */
  $: floatAbove = `bottom: ${$summaryBarHeight + 16}px;`;
</script>

<!-- Minimized FAB (hidden when AI mode is active with full-width chat) -->
{#if !$showAIChat && !$aiModeActive}
  <button
    class="group fixed right-4 z-50 flex h-12 w-12 items-center justify-center rounded-surface bg-accent-600 text-white shadow-raised transition-colors hover:bg-accent-700 sm:right-6 sm:h-14 sm:w-14"
    style={floatAbove}
    on:click={() => showAIChat.set(true)}
    title={$_('ai.chat.title')}
    transition:scale={{ duration: 200, easing: cubicOut }}
  >
    <svg class="h-6 w-6 sm:h-7 sm:w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M13 10V3L4 14h7v7l9-11h-7z"
      />
    </svg>
    {#if $aiChatActive}
      <span
        class="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-accent-400 ring-2 ring-white"
        transition:scale={{ duration: 150 }}
      >
        <span class="h-2 w-2 animate-pulse rounded-full bg-white"></span>
      </span>
    {/if}
  </button>
{/if}

<!-- Expanded chat overlay (hidden when AI mode is active with full-width chat) -->
{#if $showAIChat && !$aiModeActive}
  <div
    class="fixed right-2 z-50 flex w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-surface border border-hairline bg-white shadow-raised sm:right-4 sm:w-[400px] md:right-6"
    style="max-height: min(75vh, 580px); {floatAbove}"
    transition:fly={{ y: 30, duration: 250, easing: cubicOut }}
  >
    <!-- Header -->
    <div class="relative flex items-center justify-between border-b border-hairline px-4 py-3.5">
      <div class="flex items-center gap-2.5">
        <div
          class="flex h-8 w-8 items-center justify-center rounded-control bg-accent-50 text-accent-600"
        >
          <svg class="h-4.5 w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-semibold leading-tight text-gray-900"
            >{$_('ai.chat.title')}</span
          >
        </div>
      </div>
      <div class="flex items-center gap-0.5">
        {#if $aiChatActive}
          <button
            type="button"
            on:click={() => chatContent?.resetChat()}
            class="rounded-control p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
            title={$_('ai.chat.reset')}
            aria-label={$_('ai.chat.reset')}
          >
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        {/if}
        <button
          type="button"
          on:click={() => showAIChat.set(false)}
          class="rounded-control p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
          title={$_('buttons.close')}
          aria-label={$_('buttons.close')}
        >
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Chat content -->
    <AIChatContent bind:this={chatContent} fullWidth={false} />
  </div>
{/if}
